// src/index.tsx
import * as React from 'react';
import { Outlet, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom';
import '@patternfly/react-core/dist/styles/base.css';
import '@app/app.css';

import { AppLayout } from '@app/AppLayout/AppLayout';
import { flattenedRoutes } from '@app/routes';
import { AuthProvider, useAuth } from './components/AuthContext';
import { LoadingProvider } from './AppLayout/LoadingContext';
import { LandingPage } from './components/LandingPage';
import { NotFound } from '@app/NotFound/NotFound';
import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Spinner
} from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { FeedbackBalloon } from './components/FeedbackBalloon';
import { NewVersionAlert } from './components/NewVersionAlert';
import { EntityUpdateStream } from './components/EntityUpdateStream';
import { NotificationStream } from './components/NotificationStream';
import { ErrorBoundary } from './components/ErrorBoundary';

const AppEntry: React.FC = () => {
  const { authenticated, initialized, keycloak, authError } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (initialized && !authenticated && location.pathname !== "/") {
      // Safely call login using optional chaining in case keycloak isn't attached yet
      keycloak?.login();
    }
  }, [initialized, authenticated, location.pathname, keycloak]);

  // Sign-in never got off the ground: the one case that warrants taking over the
  // screen, since nothing at all can be rendered without a session. Left as a
  // spinner it looks like the app is merely slow, and the user waits forever.
  if (authError) {
    return (
      <Bullseye>
        <EmptyState
          status="danger"
          headingLevel="h1"
          icon={ExclamationCircleIcon}
          titleText="The application cannot start"
        >
          <EmptyStateBody>
            {authError.message} Please try again in a moment, or contact support if it persists.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => window.location.reload()}>Try again</Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      </Bullseye>
    );
  }

  // Block the entire UI from rendering (or routing) until Keycloak is ready
  if (!initialized) {
    return <Bullseye><Spinner /></Bullseye>;
  }

  // Once initialized, if still not authenticated, drop them at the Landing Page
  if (!authenticated) {
    return <LandingPage />;
  }

  return (
    <>
      <AppLayout>
        {/* A crash in a page is contained here: the layout, navigation and any
            cached data survive, and moving to another route clears it. */}
        <ErrorBoundary resetKey={location.pathname}>
          {/* The Outlet renders the component matched by the router children */}
          <Outlet />
        </ErrorBoundary>
      </AppLayout>
      <FeedbackBalloon />
      {/* Outside AppLayout on purpose — see the note in NewVersionAlert about Page trapping
          fixed children below the sidebar. Same reason FeedbackBalloon sits out here. */}
      <NewVersionAlert />
    </>
  );
};

const App: React.FunctionComponent = () => {
  // Define the router inside the component so it can access context if needed, 
  // but memoize it so it isn't recreated on every render.
  const router = React.useMemo(() => createBrowserRouter([
    {
      path: '/',
      element: <AppEntry />,
      children: [
        ...flattenedRoutes.map(route => ({
          path: route.path,
          element: route.element,
        })),
        { path: '*', element: <NotFound /> }
      ]
    }
  ]), []);

  return (
    <>
      <LoadingProvider>
        <AuthProvider>
          <RouterProvider router={router} />
          <EntityUpdateStream />
          {/* The bell's stream. Its own connection, addressed per user — see NotificationStream. */}
          <NotificationStream />
        </AuthProvider>
      </LoadingProvider>
    </>
  );
};

export default App;